/**
 * CodeEditorPage
 *
 * Main code editor interface for working with and improving a codebase.
 * This page allows users to:
 * - View and edit code files
 * - Run quality checks
 * - View performance metrics and system alerts
 *
 * It acts as the central workspace for debugging and improving Gemini prompts.
 *
 * Refactored to use three custom hooks:
 * - useCodeEditor: manages code content, framework, auto-save
 * - useDataset: manages file uploads and cloud storage
 * - useTrainingMetrics: manages training stats and quality checks
 *
 * @returns The full code editor and interface.
 */
import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import Header from "../ui/Header";
import Footer from "../ui/Footer";
import NavBar from "../ui/NavBar";
import Swal from "sweetalert2";
import VerticalProgressBar from "../ui/VerticalProgressBar";
import { Editor } from "@monaco-editor/react";
import {
  Play,
  Upload,
  Cloud,
  FileText,
  Code as CodeIcon,
  X,
  Check,
  Clock,
  AlertCircle,
  Save,
} from "lucide-react";
import codebaseService from "../../services/codebaseService";
import runService from "../../services/runService";
import { ChatContext } from "../editor/ChatContext";
import StatCard from "./StatCard";
import QualityCheckRow from "./QualityCheckRow";
import CloudFileBrowserModal from "./CloudFileBrowserModal";
import ProjectRequiredPopup from "../ui/ProjectRequiredPopup";
import ReactMarkdown from "react-markdown";
import FeedbackPopup from "../ui/FeedbackPopup";
import { useUnsavedChanges } from "../editor/UnsavedChangesContext";
import { useCodeEditor } from "../../hooks/useCodeEditor";
import { useDataset } from "../../hooks/useDataset";
import { useTrainingMetrics } from "../../hooks/useTrainingMetrics";

// Reusable tooltip bubble for explaining key ML metrics in beginner-friendly language.
const InfoBubble = ({ text, enabled = true }) => {
  if (!enabled) return null;

  return (
    <span className="relative inline-flex items-center group ml-1 align-middle">
      <span className="flex items-center justify-center w-4 h-4 text-[10px] font-bold text-sky-700 bg-sky-100 border border-sky-300 rounded-full cursor-help">
        ?
      </span>

      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 w-60 rounded-lg bg-slate-900 text-white text-xs font-normal leading-relaxed px-3 py-2 shadow-lg normal-case tracking-normal">
        {text}
      </span>
    </span>
  );
};

const CodeEditorPage = () => {
  const { codebaseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user_id = sessionStorage.getItem("userId");
  const session_token = sessionStorage.getItem("session_token");

  // Contexts
  const {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    invokeSaveCallback,
    setSaveCallback,
  } = useUnsavedChanges();

  const {
    chatbox,
    setChatbox,
    chatHistory,
    handleChatbox,
    resetChat,
    geminiLoading,
    selectedModel,
    setSelectedModel,
    availableModels,
    runId,
    activateRun,
    loadRun,
    isSwitchingModel,
  } = useContext(ChatContext);

  // Resume the run tied to this codebase (or a specific past run if ?runId= is
  // present, e.g. from the "Continue Chat" action in Run History) so chat
  // context survives refreshes and re-uploads of the same project.
  useEffect(() => {
    if (!codebaseId) return;

    const requestedRunId = searchParams.get("runId");

    if (requestedRunId) {
      loadRun(requestedRunId, codebaseId);
    } else {
      activateRun(codebaseId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codebaseId]);

  // Custom hooks for editor, dataset, and metrics
  const codeEditor = useCodeEditor(codebaseId, user_id);

  const dataset = useDataset(codebaseId, user_id, ({ content: fileContent }) => {
    const sections = Object.keys(codeEditor.content);

    if (sections.length > 0) {
      const targetSection = sections[0];

      codeEditor.saveCode(targetSection, fileContent);

      if (codeEditor.editorRefs.current[targetSection]) {
        codeEditor.editorRefs.current[targetSection].setValue(fileContent);
      }
    }
  });

  const metrics = useTrainingMetrics();

  // Local state for code execution and UI
  const [isCodeRunning, setIsCodeRunning] = useState(false);
  const [isSavingSession, setIsSavingSession] = useState(false);
  const [codeOutput, setCodeOutput] = useState("");
  const [img, setImg] = useState("");
  const [progress, setProgress] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);

  const [selectedDatasetType, setSelectedDatasetType] = useState("csv");
  const [huggingFaceUrl, setHuggingFaceUrl] = useState("");

  const [showProjectPopup, setShowProjectPopup] = useState(false);
  // Parks the promise resolver so the popup's buttons can answer the awaiting caller.
  const projectPopupResolveRef = useRef(null);

  const openProjectPopup = () =>
    new Promise((resolve) => {
      projectPopupResolveRef.current = resolve;
      setShowProjectPopup(true);
    });

  const closeProjectPopup = (resultId = null) => {
    setShowProjectPopup(false);

    if (projectPopupResolveRef.current) {
      projectPopupResolveRef.current(resultId);
      projectPopupResolveRef.current = null;
    }
  };

  // Save and Run both need a real project; prompt for one if the editor isn't in a project yet.
  // Returns null when the user cancels, so callers can bail without treating it as an error.
  const getOrCreateCodebaseId = async () => {
    if (codebaseId) return codebaseId;

    return openProjectPopup();
  };

  const datasetTypeOptions = {
    csv: {
      label: "CSV",
      accept: ".csv",
    },
    text: {
      label: "Plain Text",
      accept: ".txt",
    },
    excel: {
      label: "Excel",
      accept: ".xlsx,.xls",
    },
    json: {
      label: "JSON",
      accept: ".json",
    },
    huggingface: {
      label: "HuggingFace",
      accept: "",
    },
  };

  /* Progress bar use effect method: Gradually advance the execution progress bar while code is running.
     and stops at 92% until the backend request actually finishes. */
  useEffect(() => {
    if (!isCodeRunning) {
      return;
    }

    const progressInterval = setInterval(() => {
      setProgress((currentProgress) => {
        // Advance quickly during the beginning of execution.
        if (currentProgress < 30) {
          return Math.min(currentProgress + 5, 30);
        }

        // Continue at a moderate speed.
        if (currentProgress < 60) {
          return Math.min(currentProgress + 3, 60);
        }

        // Slow down as execution continues.
        if (currentProgress < 80) {
          return Math.min(currentProgress + 1.5, 80);
        }

        // Slowly approach completion without reaching 100%.
        if (currentProgress < 92) {
          return Math.min(currentProgress + 0.5, 92);
        }

        // Wait here until the backend request completes.
        return currentProgress;
      });
    }, 500);

    return () => {
      clearInterval(progressInterval);
    };
  }, [isCodeRunning]);

  // Fullscreen overflow prevention
  useEffect(() => {
    if (codeEditor.fullscreenSection) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [codeEditor.fullscreenSection]);

  // Handle browser reload/close warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Handle back button with unsaved changes confirmation
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    // Push a dummy state so we can intercept the back button
    window.history.pushState(null, "", window.location.pathname);

    const handlePopState = async () => {
      const result = await Swal.fire({
        title: "Unsaved Changes",
        text: "There are unsaved changes in your project. Would you like to save them or discard?",
        icon: "warning",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Save",
        denyButtonText: "Discard",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        denyButtonColor: "#d33",
      });

      if (result.isConfirmed) {
        try {
          await invokeSaveCallback();
          window.history.back();
        } catch (error) {
          // Save didn't go through (cancelled or failed) — stay put and re-arm the trap.
          if (!error?.cancelled) {
            Swal.fire({
              icon: "error",
              title: "Save Failed",
              text: "Could not save your session. Please try again.",
              confirmButtonText: "OK",
            });
          }

          window.history.pushState(null, "", window.location.pathname);
        }
      } else if (result.isDenied) {
        window.history.back();
      } else {
        // Cancel - push state again to re-arm the trap
        window.history.pushState(null, "", window.location.pathname);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);

      // Close any leftover popup so it doesn't follow the user to the next page.
      Swal.close();
    };
  }, [hasUnsavedChanges, invokeSaveCallback]);

  // Restore a previously saved session (metrics, output, etc.) when reopening a codebase.
  // Code content itself is restored inside useCodeEditor.
  // Chat is restored independently through model-specific runs in ChatContext.
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // A draft typed in another project shouldn't follow into this one
        setChatbox("");

        if (!codebaseId) {
          resetChat();
          return;
        }

        const savedSession = await codebaseService.getSession(codebaseId);
        const sessionData = savedSession?.session_data;

        if (!sessionData) return;

        if (
          sessionData.framework &&
          sessionData.framework !== codeEditor.framework
        ) {
          codeEditor.handleFrameworkSwitch(sessionData.framework);
        }

        if (sessionData.mode) {
          codeEditor.setMode(sessionData.mode);
          sessionStorage.setItem("mode", sessionData.mode);
        }

        if (sessionData.trainingStats) {
          metrics.setTrainingStats(sessionData.trainingStats);
        }

        if (sessionData.qualityChecks) {
          metrics.setQualityChecks(sessionData.qualityChecks);
        }

        if (sessionData.lastRunTime) {
          metrics.setLastRunTime(sessionData.lastRunTime);
        }

        if (sessionData.codeOutput) {
          setCodeOutput(sessionData.codeOutput);
        }

        if (sessionData.img) {
          setImg(sessionData.img);
        }

        // Demo project: attach the seeded sample dataset so the first run Code works without any setup
        if (sessionData.demoDataset?.content) {
          dataset.setUploadedFile({
            name: sessionData.demoDataset.name,
            content: sessionData.demoDataset.content,
            fileType: "csv",
          });

          dataset.parseCSVPreview(sessionData.demoDataset.content);
        }

        // Greet first-time users once per account
        const demoWelcomeKey = `demoWelcomeShown_${user_id}`;

        if (sessionData.isDemo && !localStorage.getItem(demoWelcomeKey)) {
          localStorage.setItem(demoWelcomeKey, "true");

          const userName = sessionStorage.getItem("userName") || "there";

          Swal.fire({
            icon: "info",
            titleText: `Welcome to DEEBug, ${userName}! 👋`,
            html:
              "This demo project is ready to go:<br/><br/>" +
              "<b>1.</b> The code below is a complete ML pipeline.<br/><br/>" +
              "<b>2.</b> A sample dataset is already attached — the preview shows " +
              "what a valid CSV looks like (a header row plus rows of comma-separated values).<br/><br/>" +
              "<b>3.</b> Click <b>Run Code</b> and watch the Output Console and dashboard!",
            confirmButtonText: "Let's go!",
          });
        }
      } catch (error) {
        console.error("Error restoring saved session:", error);
      }
    };

    restoreSession();
    // Only on project change — the other deps would re-run this and wipe live edits.
  }, [codebaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push the dataset to the cloud first so it's actually saved, not just named.
  const performFullSave = async (targetCodebaseId) => {
    if (dataset.uploadedFile?.content && !dataset.uploadedFile?.dataset_id) {
      await dataset.uploadToCloud();
    }

    const sessionToken = sessionStorage.getItem("session_token");

    const sessionData = {
      content: codeEditor.content,
      framework: codeEditor.framework,
      mode: codeEditor.mode,
      trainingStats: metrics.trainingStats,
      qualityChecks: metrics.qualityChecks,
      lastRunTime: metrics.lastRunTime,
      codeOutput,
      img,
      datasetName: dataset.uploadedFile?.name || null,
      savedAt: Date.now(),
    };

    await codebaseService.saveSession(
      user_id,
      targetCodebaseId,
      sessionToken,
      sessionData
    );

    // Save worked, so stop asking about unsaved changes.
    setHasUnsavedChanges(false);

    // String() avoids a false mismatch — codebaseId from the URL is a string, targetCodebaseId might not be.
    if (String(targetCodebaseId) !== String(codebaseId)) {
      const currentSection = window.location.pathname.split("/").pop();

      navigate(`/codebase/edit/${targetCodebaseId}/${currentSection}`, {
        replace: true,
      });
    }
  };

  const saveSession = async () => {
    const targetId = await getOrCreateCodebaseId();

    if (!targetId) return; // user cancelled the create/select prompt

    try {
      setIsSavingSession(true);

      await performFullSave(targetId);

      Swal.fire({
        icon: "success",
        title: "Session Saved",
        text: "Reopen it anytime from My Codebases.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error saving session:", error);

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: "Could not save your session. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSavingSession(false);
    }
  };

  // Feeds Header.jsx's Save option — re-registers every render on purpose, so it always has fresh state.
  useEffect(() => {
    setSaveCallback(async () => {
      const targetId = await getOrCreateCodebaseId();

      if (!targetId) {
        const cancelled = new Error("Save cancelled by user");
        cancelled.cancelled = true;
        throw cancelled;
      }

      await performFullSave(targetId);
    });

    return () => setSaveCallback(null);
  });

  const runCode = async () => {
    // Kept outside the try block so the catch block can still see which id we used.
    let effectiveCodebaseId = codebaseId;

    try {
      if (!dataset.uploadedFile?.content) {
        Swal.fire({
          icon: "warning",
          title: "Dataset Required",
          text: "Please upload a dataset before running the code.",
          confirmButtonText: "OK",
        });

        return;
      }

      // Run Code needs a real project too (same as Save), or the backend 400s.
      effectiveCodebaseId = await getOrCreateCodebaseId();

      if (!effectiveCodebaseId) return; // user cancelled the prompt

      const start = Date.now();

      // Start the visual progress bar near the left edge.
      setProgress(5);

      setIsCodeRunning(true);
      setCodeOutput("Running code... Please wait.");
      setImg("");

      if (!codeEditor.content || Object.keys(codeEditor.content).length === 0) {
        throw new Error("No code content available.");
      }

      if (!user_id) {
        throw new Error("User ID is missing. Please log in again.");
      }

      // Analyze dataset before running
      if (dataset.uploadedFile?.content) {
        const dataInfo = metrics.analyzeDataset(dataset.uploadedFile.content);

        metrics.setTrainingStats((prev) => ({
          ...prev,
          dataVolume: {
            rows: dataInfo.rows,
            quality: dataInfo.rows > 0 ? "Clean" : "No data",
            features: dataInfo.features,
            badgeColor:
              dataInfo.rows > 0
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-100 text-gray-600",
          },
        }));
      }

      // Capture the code first — navigating before this races with the content reload and corrupts it.
      const fullCode = Object.keys(codeEditor.content)
        .map((section) => `# Section: ${section}\n${codeEditor.content[section]}`)
        .join("\n\n");

      // Always update the URL, even on failure, so retries don't create duplicate projects.
      if (String(effectiveCodebaseId) !== String(codebaseId)) {
        const currentSection = window.location.pathname.split("/").pop();

        navigate(`/codebase/edit/${effectiveCodebaseId}/${currentSection}`, {
          replace: true,
        });
      }

      console.log("=== Running Code ===");
      console.log("User ID:", user_id);
      console.log("Codebase ID:", effectiveCodebaseId);
      console.log("Type (mode):", codeEditor.mode);
      console.log("Data file path:", dataset.uploadedFile?.name || "");
      console.log("Code Sections:", Object.keys(codeEditor.content));

      const runResult = await codebaseService.runCode(
        fullCode,
        user_id,
        effectiveCodebaseId,
        codeEditor.mode,
        dataset.uploadedFile?.name || "",
        dataset.uploadedFile?.dataset_id,
        dataset.uploadedFile?.content || null
      );

      const outputObj = {
        stdout: runResult.stdout || runResult.output || "",
        stderr: runResult.stderr || "",
      };

      setCodeOutput(outputObj);

      const outputString = outputObj.stdout + "\n" + outputObj.stderr;

      metrics.analyzeDataQuality(codeEditor.content, outputString);

      const metricsData = metrics.parseTrainingMetrics(outputString);

      if (metricsData) {
        // Calculate overfitting risk
        const overfittingInfo = metrics.calculateOverfittingRisk(
          metricsData.trainAccuracy,
          metricsData.valAccuracy
        );

        // Update training stats
        metrics.setTrainingStats((prev) => ({
          ...prev,
          modelHealth: {
            status: "Training Complete",
            risk: overfittingInfo.risk,
            gap: `Gap ${overfittingInfo.gap}%`,
            badgeColor: overfittingInfo.badgeColor,
          },
          epochs: {
            count: metricsData.epochs,
            intensity:
              metricsData.epochs > 50
                ? "Intense"
                : metricsData.epochs > 10
                  ? "Moderate"
                  : "Light",
            batchSize: metricsData.batchSize,
            badgeColor:
              metricsData.epochs > 50
                ? "bg-yellow-100 text-yellow-700"
                : "bg-blue-100 text-blue-600",
          },
        }));
      }

      // Update last run time
      const currentTime = Date.now();

      metrics.setLastRunTime(currentTime);

      metrics.setTrainingStats((prev) => ({
        ...prev,
        lastRun: {
          time: metrics.getTimeAgo(currentTime),
          status: "Live",
          pipeline: "v1.3",
          badgeColor: "bg-green-100 text-green-700",
        },
      }));

      setProgress(60);

      if (runId) {
        runService
          .saveRunResult(runId, user_id, session_token, {
            status: "success",
            stdout: outputObj.stdout,
            stderr: outputObj.stderr,
            request_id: runResult.request_id,
            epochs: metricsData?.epochs ?? null,
            batch_size: metricsData?.batchSize ?? null,
            train_accuracy: metricsData?.trainAccuracy ?? null,
            val_accuracy: metricsData?.valAccuracy ?? null,
            f1_score: metricsData?.f1Score ?? null,
          })
          .catch((error) =>
            console.error("Error saving run result:", error)
          );
      }

      if (runResult.image) {
        setImg(runResult.image);
      } else {
        try {
          const plotData = await codebaseService.getPlot(
            user_id,
            effectiveCodebaseId
          );

          if (plotData?.image) {
            setImg(plotData.image);
          }
        } catch (plotError) {
          console.log("No plot generated:", plotError);
        }
      }

      const duration = Date.now() - start;

      console.log(`Execution completed in ${duration}ms`);

      // On the very first successful run, guide the user to the right-hand
      // panel. New users often dismiss the welcome popup and never realize
      // their results (console, graphs, metrics) show up on the right.
      const outputHintKey = `outputHintShown_${user_id}`;

      if (!localStorage.getItem(outputHintKey)) {
        localStorage.setItem(outputHintKey, "true");

        Swal.fire({
          icon: "success",
          title: "Your results are ready! 👉",
          html:
            `Completed in ${(duration / 1000).toFixed(2)}s.<br/><br/>` +
            "Your results are on the <b>right side</b> of the page:<br/><br/>" +
            "• <b>Output Console</b> — the log from your run, with the training plot right below it<br/>" +
            "• <b>Result Dashboard</b> — model and training metrics<br/>" +
            "• <b>Data Quality Checks</b> — ML best-practice checks on your data<br/><br/>" +
            "Have a look on the right each time you run your code!",
          confirmButtonText: "Got it!",
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Completed in ${(duration / 1000).toFixed(2)}s`,
          timer: 2000,
          showConfirmButton: false,
        });
      }

      // Once per session, delayed 30s so it doesn't cut across reading the results.
      if (!sessionStorage.getItem("feedbackShown")) {
        sessionStorage.setItem("feedbackShown", "true");

        setTimeout(() => setShowFeedback(true), 30000);
      }
    } catch (error) {
      console.error("=== Error Running Code ===");
      console.error("Full Error:", error);
      console.error("Response:", error.response);
      console.error("Response Data:", error.response?.data);

      if (runId) {
        const errorDetails = error.response?.data?.error?.details || {};

        runService
          .saveRunResult(runId, user_id, session_token, {
            status: "failed",
            stdout: errorDetails.stdout || "",
            stderr: errorDetails.stderr || error.message || "",
            request_id: errorDetails.request_id,
          })
          .catch((err) =>
            console.error("Error saving run result:", err)
          );
      }

      let errorMsg = "Execution failed:\n\n";

      if (error.response) {
        const status = error.response.status;

        errorMsg += `HTTP ${status} Error\n\n`;

        if (status === 400) {
          errorMsg += "Bad Request - Check:\n";
          errorMsg += `• user_id: ${user_id ? "✓" : "✗ MISSING"}\n`;
          errorMsg += `• codebase_id: ${
            effectiveCodebaseId ? "✓" : "✗ MISSING"
          }\n`;
          errorMsg += `• type: ${codeEditor.mode ? "✓" : "✗ MISSING"}\n`;
          errorMsg += `• data_file_path: ${
            dataset.uploadedFile?.name || "(empty)"
          }\n`;
          errorMsg += `Backend says: ${JSON.stringify(error.response.data)}`;
        } else if (status === 408) {
          const data = error.response.data;

          const timeoutSecs =
            data?.error?.details?.timeout_seconds || 600;

          const timeoutMins = Math.round(timeoutSecs / 60);

          setCodeOutput({
            stdout: `⏱ Execution timed out after ${timeoutMins} minute(s).\n\nTips to fix:\n• Reduce the number of epochs\n• Use a smaller dataset\n• Simplify your model architecture`,
            stderr: "",
          });

          Swal.fire({
            icon: "warning",
            title: "Execution Timed Out",
            text: `Your code exceeded the ${timeoutMins}-minute time limit and was stopped. Try reducing epochs or dataset size.`,
            confirmButtonText: "OK",
          });

          return;
        } else if (status === 500) {
          errorMsg += "Backend Python Error - Possible causes:\n";
          errorMsg += "• Syntax error or variable reference error\n";
          errorMsg += "• Missing libraries or data files\n\n";

          const data = error.response.data;

          if (data && data.error) {
            const msg = data.error.message || "Unknown Error";
            const trace = data.error.details?.stderr || "";

            setCodeOutput({
              stdout: `Error: ${msg}`,
              stderr: trace,
            });

            return;
          }
        } else {
          errorMsg +=
            error.response.data?.message ||
            error.response.data?.error ||
            "Unknown error";
        }
      } else if (error.request) {
        errorMsg += "No response from server. Check:\n";
        errorMsg += "• Backend server is running\n";
        errorMsg += "• Network connection\n";
        errorMsg += "• CORS settings\n";
      } else {
        errorMsg += error.message;
      }

      console.log("Final Error Message:", errorMsg);

      setCodeOutput(errorMsg);

      Swal.fire({
        icon: "error",
        title: "Execution Failed",
        text: "Check the output console for details",
        confirmButtonText: "OK",
      });
    } finally {
      // Complete the progress bar after the backend succeeds or fails.
      setProgress(100);

      // Keep the completed bar visible briefly before hiding it.
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsCodeRunning(false);
      setProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800">
      <FeedbackPopup
        open={showFeedback}
        closePopup={() => setShowFeedback(false)}
      />

      <Header />

      <NavBar currentStep={0} />

      {/* MODEL AND TRAINING METRICS */}
      <div className="w-full px-4 lg:px-10 mt-6 lg:mt-10">
        <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
          {/* DASHBOARD STATS CARDS */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-yellow-600 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5 text-white"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 13h4v8H3v-8zm7-6h4v14h-4V7zm7 3h4v11h-4V10z"
                />
              </svg>
            </div>

            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800">
                Model and Training Metrics
              </h2>

              {isCodeRunning && (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-100 border border-teal-300 px-2 py-0.5 rounded-full">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-600"></span>
                  </span>
                  Running
                </span>
              )}
            </div>
          </div>

          {isCodeRunning && (
            <div className="relative overflow-hidden rounded-xl border border-teal-300 bg-gradient-to-r from-teal-50 via-cyan-50 to-teal-50 p-4 flex items-center gap-4 shadow-sm">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-teal-500 shadow-md">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />

                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-teal-800">
                  Code is running… {Math.round(progress)}%
                </p>

                <p className="text-xs text-teal-600">
                  Metrics below will refresh automatically when execution
                  finishes
                </p>
              </div>

              <div className="absolute bottom-0 left-0 h-1 w-full bg-teal-100 overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full transition-[width] duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          <div
            className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 transition-opacity ${
              isCodeRunning ? "opacity-60" : "opacity-100"
            }`}
          >
            <StatCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="w-6 h-6"
                >
                  <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.656l-6.828-6.828a4 4 0 010-5.656z" />
                </svg>
              }
              title={
                <>
                  Model Health
                  <InfoBubble
                    enabled={showTooltips}
                    text="Shows whether the model is training normally or may have issues like overfitting."
                  />
                </>
              }
              main={metrics.trainingStats.modelHealth.status}
              badge={metrics.trainingStats.modelHealth.risk}
              badgeColor={metrics.trainingStats.modelHealth.badgeColor}
              sub={metrics.trainingStats.modelHealth.gap}
              showTooltips={showTooltips}
              tooltip="Shows whether the model is training normally or may have issues like overfitting."
            />

            <StatCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="w-6 h-6"
                >
                  <path d="M5 2a1 1 0 011-1h2a1 1 0 011 1v2h2V2a1 1 0 011-1h2a1 1 0 011 1v2h1.5a.5.5 0 01.5.5V7h-15V4.5a.5.5 0 01.5-.5H5V2zm-3 6h16v10H2V8zm4 2a1 1 0 000 2h8a1 1 0 100-2H6z" />
                </svg>
              }
              title={
                <>
                  Training Epochs
                  <InfoBubble
                    enabled={showTooltips}
                    text="An epoch is one full pass through the training dataset."
                  />
                </>
              }
              main={
                metrics.trainingStats.epochs.count > 0
                  ? metrics.trainingStats.epochs.count.toString()
                  : "Not started"
              }
              badge={metrics.trainingStats.epochs.intensity}
              badgeColor={metrics.trainingStats.epochs.badgeColor}
              sub={
                metrics.trainingStats.epochs.batchSize > 0
                  ? `Batch ${metrics.trainingStats.epochs.batchSize}`
                  : "N/A"
              }
              showTooltips={showTooltips}
              tooltip="An epoch is one full pass through the training dataset."
            />

            <StatCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="w-6 h-6"
                >
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0v4a.75.75 0 00.356.638l2.5 1.5a.75.75 0 10.748-1.296L10.75 9.28V5.75z" />
                </svg>
              }
              title={
                <>
                  Last Run
                  <InfoBubble
                    enabled={showTooltips}
                    text="Shows when your code or model was last executed."
                  />
                </>
              }
              main={metrics.getTimeAgo(metrics.lastRunTime)}
              badge={metrics.trainingStats.lastRun.status}
              badgeColor={metrics.trainingStats.lastRun.badgeColor}
              sub={metrics.trainingStats.lastRun.pipeline}
              showTooltips={showTooltips}
              tooltip="Shows when your code or model was last executed."
            />

            <StatCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="w-6 h-6"
                >
                  <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v1H4V4zm-1 3h14v2H3V7zm0 4h14v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3z" />
                </svg>
              }
              title={
                <>
                  Data Volume
                  <InfoBubble
                    enabled={showTooltips}
                    text="Shows how much dataset information is being used, such as rows and features."
                  />
                </>
              }
              main={
                metrics.trainingStats.dataVolume.rows > 0
                  ? `${(metrics.trainingStats.dataVolume.rows / 1000).toFixed(
                      1
                    )}k rows`
                  : "No data"
              }
              badge={metrics.trainingStats.dataVolume.quality}
              badgeColor={metrics.trainingStats.dataVolume.badgeColor}
              sub={
                metrics.trainingStats.dataVolume.features > 0
                  ? `${metrics.trainingStats.dataVolume.features} features`
                  : "N/A"
              }
              showTooltips={showTooltips}
              tooltip="Shows how much dataset information is being used, such as rows and features."
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row w-full px-4 lg:px-10 gap-6 mt-6 lg:mt-10">
        {/* LEFT — EDITOR */}
        <div className="w-full lg:w-3/5">
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 lg:p-8">
            {/* HEADER */}
            <div className="flex flex-col gap-4 mb-6">
              <div>
                <span className="block text-slate-500 text-xs font-semibold uppercase tracking-wide mb-1">
                  Workspace
                </span>

                <h1 className="text-2xl font-bold text-slate-900">
                  Code Editor
                </h1>

                <p className="text-slate-500 text-xs mt-2">
                  Edit and execute your ML pipeline
                </p>

                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs font-medium text-slate-600">
                    Tooltips
                  </span>

                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowTooltips(true)}
                      className={`px-3 py-1 rounded-l-full text-xs font-semibold border border-slate-300 transition ${
                        showTooltips
                          ? "bg-sky-100 text-sky-700"
                          : "bg-white text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      On
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowTooltips(false)}
                      className={`px-3 py-1 rounded-r-full text-xs font-semibold border border-l-0 border-slate-300 transition ${
                        !showTooltips
                          ? "bg-slate-200 text-slate-700"
                          : "bg-white text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>
              </div>

              {/* FRAMEWORK SELECTOR */}
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-medium text-slate-600">
                  Framework:
                </span>

                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      codeEditor.handleFrameworkSwitch("tensorflow")
                    }
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      codeEditor.framework === "tensorflow"
                        ? "bg-orange-500 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    TensorFlow
                  </button>

                  <button
                    onClick={() =>
                      codeEditor.handleFrameworkSwitch("pytorch")
                    }
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      codeEditor.framework === "pytorch"
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    PyTorch
                  </button>
                </div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <select
                value={selectedDatasetType}
                onChange={(e) => {
                  setSelectedDatasetType(e.target.value);

                  if (e.target.value !== "huggingface") {
                    setHuggingFaceUrl("");
                  }
                }}
                className="w-full border border-slate-300 bg-white text-slate-700 font-semibold px-3 py-2 rounded-lg shadow-sm transition cursor-pointer text-sm"
                aria-label="Select dataset file type"
              >
                {Object.entries(datasetTypeOptions).map(([value, option]) => (
                  <option key={value} value={value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {selectedDatasetType === "huggingface" && (
                <>
                  <input
                    type="text"
                    value={huggingFaceUrl}
                    onChange={(e) => setHuggingFaceUrl(e.target.value)}
                    placeholder="Enter Hugging Face dataset ID or URL"
                    className="border border-slate-300 bg-white text-slate-700 px-3 py-2 rounded-lg shadow-sm text-sm min-w-[280px]"
                    aria-label="Hugging Face dataset ID or URL"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      dataset.handleHuggingFaceDatasetLoad(huggingFaceUrl)
                    }
                    disabled={dataset.loadingFiles}
                    className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-semibold px-4 py-2 rounded-lg shadow transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Cloud size={16} />
                    {dataset.loadingFiles ? "Loading..." : "Load Dataset"}
                  </button>
                </>
              )}

              {selectedDatasetType !== "huggingface" && (
                <label className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg shadow transition cursor-pointer text-sm">
                  <Upload size={16} />
                  Database Upload

                  <input
                    type="file"
                    accept={datasetTypeOptions[selectedDatasetType].accept}
                    onChange={(e) =>
                      dataset.handleLocalFileUpload(e, selectedDatasetType)
                    }
                    className="hidden"
                  />
                </label>
              )}

              <label className="w-full flex items-center justify-center gap-2 bg-[#3776ab] hover:bg-[#2b5d87] text-white font-semibold px-4 py-2 rounded-lg shadow transition cursor-pointer text-sm">
                <CodeIcon size={16} />
                Upload Python

                <input
                  type="file"
                  accept=".py"
                  onChange={dataset.handlePythonUpload}
                  className="hidden"
                />
              </label>

              <button
                onClick={() => dataset.setShowFileDialog(true)}
                className="w-full flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white font-semibold px-4 py-2 rounded-lg shadow transition text-sm"
              >
                <Cloud size={16} />
                Browse Cloud
              </button>

              {dataset.uploadedFile && (
                <button
                  onClick={dataset.uploadToCloud}
                  className="w-full md:col-start-2 flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-4 py-2 rounded-lg shadow transition text-sm"
                >
                  <Upload size={16} />
                  Save to Cloud
                </button>
              )}

              <button
                onClick={saveSession}
                disabled={isSavingSession}
                className="w-full md:col-start-3 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg shadow transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Save size={16} />
                {isSavingSession ? "Saving..." : "Save Session"}
              </button>

              <button
                onClick={runCode}
                disabled={isCodeRunning}
                className="w-full flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-4 py-2 rounded-lg shadow transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Play size={16} />
                {isCodeRunning ? "Running..." : "Run Code"}
              </button>
            </div>

            {/* FILE INFO */}
            {dataset.uploadedFile && (
              <div className="flex flex-col gap-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText size={16} className="text-blue-600" />

                    <span className="text-blue-800 font-medium">
                      {dataset.uploadedFile.name}
                    </span>
                  </div>

                  <button
                    onClick={dataset.clearUploadedFile}
                    aria-label="Remove uploaded dataset"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <X size={16} />
                  </button>
                </div>

                {dataset.previewData.length > 0 && (
                  <div className="overflow-x-auto border rounded-lg bg-white shadow-sm max-h-64">
                    <table className="min-w-full divide-y divide-gray-200 text-[10px]">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {dataset.previewData[0].map((header, i) => (
                            <th
                              key={i}
                              className="px-3 py-2 text-left font-bold text-gray-600 uppercase tracking-wider border-b"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-100">
                        {dataset.previewData.slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-gray-50">
                            {row.map((cell, cellIndex) => (
                              <td
                                key={cellIndex}
                                className="px-3 py-1.5 whitespace-nowrap text-gray-500 border-r last:border-r-0"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="p-2 text-[9px] text-slate-400 text-center border-t bg-gray-50 font-medium">
                      Showing first {dataset.previewData.length} rows of the
                      dataset
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* EDITOR SECTIONS */}
            <div className="mt-6">
              {Object.keys(codeEditor.currentTemplates).map((sectionKey) => {
                const isCollapsed =
                  codeEditor.collapsedSections[sectionKey] ?? false;

                const isFullscreen =
                  codeEditor.fullscreenSection === sectionKey;

                return (
                  <div
                    key={sectionKey}
                    id={sectionKey}
                    className={
                      isFullscreen
                        ? "fixed inset-0 z-[100] bg-[#f8fafc] p-6 flex flex-col w-screen h-screen overflow-hidden"
                        : "mb-6 editor-section"
                    }
                  >
                    <div className="flex items-center justify-between mb-2">
                      {/* Live region so the save-status pill is announced without moving focus. */}
                      <div className="flex items-center gap-2" aria-live="polite">
                        <h2 className="text-lg font-semibold text-gray-800">
                          {sectionKey
                            .replace(/-/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </h2>

                        {codeEditor.saveStatus === "saved" && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            <Check size={11} />
                            Saved
                          </span>
                        )}

                        {codeEditor.saveStatus === "saving" && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-500 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full animate-pulse">
                            <Clock size={11} />
                            Saving…
                          </span>
                        )}

                        {codeEditor.saveStatus === "unsaved" && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            <AlertCircle size={11} />
                            Unsaved Changes
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => codeEditor.resetSection(sectionKey)}
                          className="text-sm font-medium text-amber-600 hover:text-amber-700 hover:underline"
                          title="Reset this section to the default template"
                        >
                          Reset
                        </button>

                        <button
                          onClick={() =>
                            codeEditor.setFullscreenSection(
                              isFullscreen ? null : sectionKey
                            )
                          }
                          className="text-sm font-medium text-sky-600 hover:underline"
                        >
                          {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        </button>

                        {!isFullscreen && (
                          <button
                            onClick={() =>
                              codeEditor.setCollapsedSections((prev) => ({
                                ...prev,
                                [sectionKey]: !prev[sectionKey],
                              }))
                            }
                            className="text-sm text-sky-600 hover:underline"
                          >
                            {isCollapsed ? "Show" : "Hide"}
                          </button>
                        )}
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div
                        className={`bg-slate-50 border rounded-lg p-3 shadow-sm ${
                          isFullscreen ? "flex-1" : ""
                        }`}
                      >
                        <Editor
                          height={
                            isFullscreen ? "calc(100vh - 120px)" : "240px"
                          }
                          defaultLanguage="python"
                          theme="vs-light"
                          value={
                            codeEditor.content[sectionKey] ??
                            codeEditor.currentTemplates[sectionKey]
                          }
                          onMount={(editor, monaco) =>
                            codeEditor.handleEditorMount(
                              editor,
                              monaco,
                              sectionKey
                            )
                          }
                          options={{
                            fontSize: 13,
                            automaticLayout: true,
                            wordWrap: "on",
                            minimap: { enabled: false },
                            scrollbar: { vertical: "auto" },
                            lineNumbers: "on",
                            readOnly: false,
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT — OUTPUT & AI */}
        <div className="w-full lg:w-2/5 flex flex-col gap-6">
          {/* AI ASSISTANT */}
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                  />
                </svg>
              </div>

              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  AI Assistant
                </h2>

                <p className="text-xs text-slate-500">
                  Get help with your code
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Model Selection
              </label>

              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={isSwitchingModel}
                className="w-full p-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white shadow-sm disabled:opacity-60 disabled:cursor-wait"
              >
                {availableModels.length > 0 ? (
                  availableModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))
                ) : (
                  <option value="">Loading models...</option>
                )}
              </select>

              {isSwitchingModel && (
                <p className="text-xs text-sky-600 mt-2">
                  Loading model conversation...
                </p>
              )}
            </div>

            <div className="mb-3">
              <textarea
                className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm resize-none shadow-sm"
                rows="4"
                value={chatbox}
                onChange={(e) => setChatbox(e.target.value)}
                placeholder="Ask about your code, errors, or optimizations..."
              />
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={handleChatbox}
                disabled={geminiLoading || isSwitchingModel}
                className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
              >
                {geminiLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />

                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>

                    Thinking...
                  </span>
                ) : (
                  "Send Message"
                )}
              </button>

              <button
                onClick={resetChat}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
              >
                Reset
              </button>
            </div>

            <div className="border-t pt-4">
              <div className="max-h-48 overflow-y-auto space-y-3">
                {chatHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-12 h-12 mx-auto mb-2 opacity-50"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
                      />
                    </svg>

                    <p>No messages yet</p>

                    <p className="text-xs mt-1">
                      Start a conversation with AI
                    </p>
                  </div>
                ) : (
                  [...chatHistory].reverse().map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg ${
                        msg.role === "user"
                          ? "bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100"
                          : "bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-bold uppercase tracking-wide ${
                            msg.role === "user"
                              ? "text-sky-700"
                              : "text-emerald-700"
                          }`}
                        >
                          {msg.role === "user" ? "You" : "AI"}
                        </span>
                      </div>

                      <div className="text-xs text-slate-700 leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* MODEL OUTPUT */}
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                  />
                </svg>
              </div>

              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  Output Console
                </h2>

                <p className="text-xs text-slate-500">
                  Execution results
                </p>
              </div>
            </div>

            {codeOutput && (
              <div className="relative p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 max-h-[280px] overflow-auto shadow-inner">
                {typeof codeOutput === "object" && codeOutput !== null ? (
                  <>
                    <pre className="whitespace-pre-wrap text-xs font-mono text-slate-700">
                      {codeOutput.stdout}
                    </pre>

                    {codeOutput.stderr && (
                      <pre className="whitespace-pre-wrap text-xs font-mono text-red-600 mt-2 border-t border-red-200 pt-2">
                        {codeOutput.stderr}
                      </pre>
                    )}
                  </>
                ) : (
                  <pre className="whitespace-pre-wrap text-xs font-mono text-slate-700">
                    {codeOutput}
                  </pre>
                )}
              </div>
            )}

            {img && (
              <div className="mt-4 relative rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white">
                <img
                  src={img}
                  alt="Visualization"
                  className="w-full max-h-[280px] object-contain"
                />
              </div>
            )}

            {!codeOutput && !img && (
              <div className="relative overflow-hidden rounded-xl border-2 border-dashed border-slate-200 bg-gradient-to-br from-slate-50 to-white">
                <div className="text-slate-400 text-sm text-center py-16">
                  <div className="inline-flex p-4 rounded-full bg-slate-100 mb-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-slate-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"
                      />
                    </svg>
                  </div>

                  <p className="font-medium text-slate-600">
                    Ready to Execute
                  </p>

                  <p className="text-xs mt-1">
                    Run your code to see output here
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* DATA QUALITY CHECKS */}
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5 text-white"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>

                <div>
                  <h2 className="text-lg font-bold text-slate-800">
                    Data Quality Checks
                  </h2>
                </div>
              </div>

              <button
                onClick={() =>
                  metrics.setShowQualityChecks(!metrics.showQualityChecks)
                }
                className="text-sm text-slate-500 hover:text-slate-700 font-medium"
              >
                {metrics.showQualityChecks ? "Hide checks" : "Show checks"}
              </button>
            </div>

            {metrics.showQualityChecks && (
              <div className="space-y-3">
                <QualityCheckRow
                  label={
                    <>
                      Feature Scaling
                      <InfoBubble
                        enabled={showTooltips}
                        text="Feature scaling makes numeric values easier for the model to compare by putting them on a similar scale."
                      />
                    </>
                  }
                  status={metrics.qualityChecks.featureScaling.status}
                  message={metrics.qualityChecks.featureScaling.message}
                />

                <QualityCheckRow
                  label={
                    <>
                      Class Balance
                      <InfoBubble
                        enabled={showTooltips}
                        text="Class balance checks whether each output category has enough examples so the model does not become biased toward one class."
                      />
                    </>
                  }
                  status={metrics.qualityChecks.classBalance.status}
                  message={metrics.qualityChecks.classBalance.message}
                />

                <QualityCheckRow
                  label={
                    <>
                      Missing Values
                      <InfoBubble
                        enabled={showTooltips}
                        text="Missing values are empty or incomplete data points that may need to be cleaned before training."
                      />
                    </>
                  }
                  status={metrics.qualityChecks.missingValues.status}
                  message={metrics.qualityChecks.missingValues.message}
                />

                <QualityCheckRow
                  label={
                    <>
                      Data Leakage
                      <InfoBubble
                        enabled={showTooltips}
                        text="Data leakage happens when information from outside the training data, such as validation or test data, is used during training and makes the model look more accurate than it really is."
                      />
                    </>
                  }
                  status={metrics.qualityChecks.dataLeakage.status}
                  message={metrics.qualityChecks.dataLeakage.message}
                />

                <QualityCheckRow
                  label={
                    <>
                      Train/Val Split
                      <InfoBubble
                        enabled={showTooltips}
                        text="Train/validation split checks whether the dataset is separated into training data and validation data to test model performance."
                      />
                    </>
                  }
                  status={metrics.qualityChecks.trainValSplit.status}
                  message={metrics.qualityChecks.trainValSplit.message}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CLOUD FILE BROWSER MODAL */}
      {dataset.showFileDialog && (
        <CloudFileBrowserModal
          onClose={() => dataset.setShowFileDialog(false)}
          loadingFiles={dataset.loadingFiles}
          currentPath={dataset.currentPath}
          bucketFiles={dataset.bucketFiles}
          onSelectFile={dataset.handleCloudFileSelect}
          onDeleteFile={dataset.handleDeleteDataset}
          onNavigateFolder={dataset.navigateFolder}
        />
      )}

      <ProjectRequiredPopup
        open={showProjectPopup}
        onClose={() => closeProjectPopup(null)}
        onProjectReady={(id) => closeProjectPopup(id)}
      />

      <VerticalProgressBar />

      <Footer />
    </div>
  );
};

export default CodeEditorPage;
